import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Spinner,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useColorModeValue as mode,
} from "@chakra-ui/react"
import { useCallback, useEffect, useMemo, useState } from "react"
import axios from "axios"
import ReactApexChart from "react-apexcharts"

const seriesTypeOptions = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
]

const categoryOrder = ["Customer", "Prospect", "Unlikely", "Irrelevant", "Spam", "Uncategorized"]

const categoryColors = {
  Customer: "#3182CE",
  Prospect: "#38A169",
  Unlikely: "#D69E2E",
  Irrelevant: "#DD6B20",
  Spam: "#E53E3E",
  Uncategorized: "#A0AEC0",
}

const CallStatisticsScreen = () => {
  const [statsData, setStatsData] = useState({ daily: null, weekly: null, monthly: null })
  const [view, setView] = useState("daily")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [selectedBucket, setSelectedBucket] = useState(null)
  const [hourlyData, setHourlyData] = useState(null)
  const [hourlyLoading, setHourlyLoading] = useState(false)
  const [hourlyError, setHourlyError] = useState(null)

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true)
      setError(null)

      try {
        const { data } = await axios.get("/api/caller-id/stats")
        setStatsData({
          daily: data.daily || null,
          weekly: data.weekly || null,
          monthly: data.monthly || null,
        })
      } catch (err) {
        const msg = err.response?.data?.message || err.message || "Failed to load phone call statistics"
        setError(msg)
      } finally {
        setLoading(false)
      }
    }

    fetchStats()
  }, [])

  const activeStats = useMemo(
    () =>
      statsData[view] || {
        categories: categoryOrder,
        buckets: [],
        totals: {
          totalCalls: 0,
          categoryTotals: categoryOrder.reduce((acc, category) => {
            acc[category] = 0
            return acc
          }, {}),
          categoryRatios: categoryOrder.reduce((acc, category) => {
            acc[category] = 0
            return acc
          }, {}),
        },
      },
    [statsData, view]
  )

  const categories = activeStats.categories || categoryOrder
  const buckets = activeStats.buckets
  const totals = activeStats.totals
  const listBuckets = useMemo(() => buckets.filter((bucket) => (bucket?.totalCalls || 0) > 0), [buckets])

  useEffect(() => {
    setSelectedBucket(null)
    setHourlyData(null)
    setHourlyError(null)
  }, [view])

  const handleSelectBucket = useCallback(async (bucket) => {
    if (!bucket?.key) {
      return
    }

    setSelectedBucket({ key: bucket.key, label: bucket.label })
    setHourlyLoading(true)
    setHourlyError(null)

    try {
      const { data } = await axios.get("/api/caller-id/stats/hourly", {
        params: {
          period: view,
          bucketKey: bucket.key,
        },
      })

      setHourlyData(data)
    } catch (requestError) {
      const msg = requestError.response?.data?.message || requestError.message || "Failed to load hourly distribution"
      setHourlyError(msg)
      setHourlyData(null)
    } finally {
      setHourlyLoading(false)
    }
  }, [view])

  const chartConfig = useMemo(() => {
    return {
      series: categories.map((category) => ({
        name: category,
        data: buckets.map((bucket) => bucket?.categoryCounts?.[category] || 0),
      })),
      options: {
        chart: {
          type: "bar",
          stacked: true,
          toolbar: { show: true },
        },
        colors: categories.map((category) => categoryColors[category] || "#A0AEC0"),
        plotOptions: {
          bar: {
            borderRadius: 4,
            columnWidth: "60%",
          },
        },
        dataLabels: {
          enabled: false,
        },
        xaxis: {
          categories: buckets.map((bucket) => bucket.label),
          labels: {
            rotate: -35,
            hideOverlappingLabels: true,
          },
        },
        yaxis: {
          title: {
            text: "Calls",
          },
        },
        legend: {
          position: "top",
          horizontalAlign: "left",
        },
        tooltip: {
          y: {
            formatter: (value) => `${value} calls`,
          },
        },
      },
    }
  }, [buckets, categories])

  const hourlyChartConfig = useMemo(() => {
    const hourlyBuckets = hourlyData?.hourly?.buckets || []
    const hourlyCategories = hourlyData?.hourly?.categories || categoryOrder

    return {
      series: hourlyCategories.map((category) => ({
        name: category,
        data: hourlyBuckets.map((bucket) => bucket?.categoryCounts?.[category] || 0),
      })),
      options: {
        chart: {
          type: "bar",
          stacked: true,
          toolbar: { show: true },
        },
        colors: hourlyCategories.map((category) => categoryColors[category] || "#A0AEC0"),
        plotOptions: {
          bar: {
            borderRadius: 4,
            columnWidth: "72%",
          },
        },
        dataLabels: { enabled: false },
        xaxis: {
          categories: hourlyBuckets.map((bucket) => bucket.label),
          labels: {
            rotate: -50,
            hideOverlappingLabels: true,
          },
        },
        yaxis: {
          title: {
            text: "Calls",
          },
        },
        legend: {
          position: "top",
          horizontalAlign: "left",
        },
      },
    }
  }, [hourlyData])

  const cardBg = mode("white", "gray.800")
  const borderColor = mode("gray.200", "gray.700")
  const tableHeaderBg = mode("gray.50", "gray.700")
  const tableRowHoverBg = mode("gray.100", "gray.600")

  return (
    <Box maxW='7xl' mx='auto' px={{ base: 4, md: 8 }} py={{ base: 6, md: 10 }}>
      <VStack align='stretch' spacing={8}>
        <Box>
          <Heading size='lg' mb={2}>
            Phone Call Statistics
          </Heading>
          <Text color='gray.600' fontSize='sm'>
            Volume and mix of incoming calls, including new callers vs existing customers
          </Text>
        </Box>

        <HStack spacing={2} flexWrap='wrap'>
          {seriesTypeOptions.map((option) => (
            <Button
              key={option.value}
              size='sm'
              colorScheme='blue'
              variant={view === option.value ? "solid" : "outline"}
              onClick={() => setView(option.value)}
            >
              {option.label}
            </Button>
          ))}
        </HStack>

        {error && (
          <Alert status='error' borderRadius='md'>
            <AlertIcon />
            {error}
          </Alert>
        )}

        <Flex gap={4} wrap='wrap'>
          <Box bg={cardBg} borderWidth='1px' borderColor={borderColor} borderRadius='lg' p={4} minW='220px' flex='1'>
            <Stat>
              <StatLabel>Total Calls</StatLabel>
              <StatNumber>{totals.totalCalls || 0}</StatNumber>
              <StatHelpText>{view.charAt(0).toUpperCase() + view.slice(1)} window</StatHelpText>
            </Stat>
          </Box>

          {categories.map((category) => (
            <Box
              key={category}
              bg={cardBg}
              borderWidth='1px'
              borderColor={borderColor}
              borderTopWidth='4px'
              borderTopColor={categoryColors[category] || "gray.400"}
              borderRadius='lg'
              p={4}
              minW='220px'
              flex='1'
            >
              <Stat>
                <StatLabel>{category}</StatLabel>
                <StatNumber>{totals?.categoryTotals?.[category] || 0}</StatNumber>
                <StatHelpText>{totals?.categoryRatios?.[category] || 0}% of total</StatHelpText>
              </Stat>
            </Box>
          ))}
        </Flex>

        <Box bg={cardBg} borderWidth='1px' borderColor={borderColor} borderRadius='lg' p={6} shadow='md'>
          {loading ? (
            <Flex justify='center' align='center' h='320px'>
              <Spinner size='lg' />
            </Flex>
          ) : buckets.length === 0 ? (
            <Flex justify='center' align='center' h='220px'>
              <Text color='gray.500'>No call history data available</Text>
            </Flex>
          ) : (
            <ReactApexChart type='bar' series={chartConfig.series} options={chartConfig.options} height={380} />
          )}
        </Box>

        <Box bg={cardBg} borderWidth='1px' borderColor={borderColor} borderRadius='lg' shadow='md' overflowX='auto'>
          <Table variant='simple' size='sm'>
            <Thead bg={tableHeaderBg}>
              <Tr>
                <Th>{view.charAt(0).toUpperCase() + view.slice(1)} Period</Th>
                <Th isNumeric>Total Calls</Th>
                <Th isNumeric>Customer</Th>
                <Th isNumeric>Prospect</Th>
                <Th isNumeric>Unlikely</Th>
                <Th isNumeric>Irrelevant</Th>
                <Th isNumeric>Spam</Th>
                <Th isNumeric>Uncategorized</Th>
              </Tr>
            </Thead>
            <Tbody>
              {loading ? (
                <Tr>
                  <Td colSpan={8} textAlign='center' py={8}>
                    <Spinner size='md' />
                  </Td>
                </Tr>
              ) : listBuckets.length === 0 ? (
                <Tr>
                  <Td colSpan={8} textAlign='center' py={8}>
                    <Text color='gray.500'>No periods with calls in this view</Text>
                  </Td>
                </Tr>
              ) : (
                listBuckets.map((bucket) => (
                  <Tr
                    key={bucket.key}
                    _hover={{ bg: tableRowHoverBg }}
                    cursor='pointer'
                    bg={selectedBucket?.key === bucket.key ? tableRowHoverBg : "transparent"}
                    onClick={() => handleSelectBucket(bucket)}
                  >
                    <Td fontWeight='medium'>{bucket.label}</Td>
                    <Td isNumeric>{bucket.totalCalls}</Td>
                    <Td isNumeric>{bucket?.categoryCounts?.Customer || 0}</Td>
                    <Td isNumeric>{bucket?.categoryCounts?.Prospect || 0}</Td>
                    <Td isNumeric>{bucket?.categoryCounts?.Unlikely || 0}</Td>
                    <Td isNumeric>{bucket?.categoryCounts?.Irrelevant || 0}</Td>
                    <Td isNumeric>{bucket?.categoryCounts?.Spam || 0}</Td>
                    <Td isNumeric>{bucket?.categoryCounts?.Uncategorized || 0}</Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </Box>

        <Text color='gray.600' fontSize='sm'>
          Click a period row in the table to view hourly call distribution.
        </Text>

        {(selectedBucket || hourlyLoading || hourlyError || hourlyData) && (
          <Box bg={cardBg} borderWidth='1px' borderColor={borderColor} borderRadius='lg' p={6} shadow='md'>
            <Heading size='md' mb={2}>
              Hourly Distribution
            </Heading>
            <Text color='gray.600' fontSize='sm' mb={4}>
              {hourlyData?.label
                ? `${hourlyData.label} (${hourlyData.hourly?.totals?.totalCalls || 0} calls)`
                : selectedBucket?.label || "Select a period"}
            </Text>

            {hourlyError && (
              <Alert status='error' borderRadius='md' mb={4}>
                <AlertIcon />
                {hourlyError}
              </Alert>
            )}

            {hourlyLoading ? (
              <Flex justify='center' align='center' h='260px'>
                <Spinner size='lg' />
              </Flex>
            ) : (hourlyData?.hourly?.buckets || []).some((bucket) => (bucket?.totalCalls || 0) > 0) ? (
              <ReactApexChart type='bar' series={hourlyChartConfig.series} options={hourlyChartConfig.options} height={320} />
            ) : (
              <Flex justify='center' align='center' h='220px'>
                <Text color='gray.500'>No calls in this period</Text>
              </Flex>
            )}
          </Box>
        )}
      </VStack>
    </Box>
  )
}

export default CallStatisticsScreen
